import createTransform from 'redux-persist/es/createTransform'
import type { SessionState } from '../types/auth.types'

/**
 * `msBackendAuthChecking` and `msBackendAuthError` are transient, per-page-load UI
 * flags — they must NOT survive a reload. If they rehydrate stale (e.g. `checking`
 * as `false`), the MsBackendAuthGuard can never observe the saga's synchronous
 * `true → false` transition, leaving the app stuck on the "Authenticating…" spinner
 * forever. Reset them on rehydration so every fresh load starts from a clean
 * "checking" state.
 *
 * IMPORTANT: redux-persist invokes a transform's outbound function ONCE PER FIELD
 * of the persisted slice, not once for the whole state object — see
 * `getStoredState.js`: `transformer.out(rawState[key], key, rawState)`. So `state`
 * here is the individual field's primitive value, and `key` is the field name.
 * (An earlier version of this transform assumed it received the whole session
 * object and guarded on `typeof state === 'object'`, which made it a silent no-op
 * for every field — always a primitive — never actually resetting anything.)
 */
export const resetTransientSessionTransform = createTransform<
  unknown,
  unknown,
  SessionState,
  SessionState
>(
  // inbound (state → storage): persist as-is
  (inboundState) => inboundState,
  // outbound (storage → state): reset the two transient fields, pass everything else through
  (outboundState, key) => {
    if (key === 'msBackendAuthChecking') return true
    if (key === 'msBackendAuthError') return null
    return outboundState
  }
)
